import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminSanctionDto, adminRevokeSanction } from '../../api/payment/brand-sanction.api';
import { isSuperAdmin } from '../../utils/admin-permissions';
import { apiErrorMessage } from '../../utils/api-error-message';
import { formatCa } from '../../utils/individual-order';
import { formatVnDate, sanctionLevelLabel, sanctionWindowLabel } from '../../utils/brand-sanction';
import { validateAdminNote } from '../../utils/brand-sanction-admin';
import AdminTotpInput from './admin-totp-input';

const INPUT =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white';

/** Lịch sử xử phạt của một người (mọi tài khoản đặt đơn của họ) ở trang admin, kèm nút gỡ cho SuperAdmin. */
const AdminSanctionHistory: React.FC<{ items: AdminSanctionDto[]; onChanged: () => void }> = ({ items, onChanged }) => {
  const [revoking, setRevoking] = useState<AdminSanctionDto | null>(null);
  const [reason, setReason] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);

  const openRevoke = (s: AdminSanctionDto) => {
    setRevoking(s);
    setReason('');
    setTotp('');
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    try {
      setBusy(true);
      await adminRevokeSanction(revoking.id, reason.trim(), totp);
      toast.success('Đã gỡ lần xử phạt.');
      setRevoking(null);
      onChanged();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Gỡ xử phạt thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) return <p className="text-sm text-gray-500">Chưa bị xử phạt lần nào.</p>;

  return (
    <div className="space-y-2">
      {items.map((s) => (
        <div
          key={s.id}
          data-testid="admin-sanction-item"
          className="rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-boxdark"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-slate-800 dark:text-white">
              {sanctionLevelLabel(s.level)} · {formatVnDate(s.createdAt)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{sanctionWindowLabel(s)}</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-200">{s.reason}</p>
          {s.lowerLevelReason && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              Chọn nhẹ hơn mức gợi ý ({sanctionLevelLabel(s.suggestedLevel)}): {s.lowerLevelReason}
            </p>
          )}
          {s.compensations.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs text-gray-600 dark:text-gray-300">
              {s.compensations.map((c) => (
                <li key={c.creatorId}>
                  Bồi thường {c.creatorName}: {formatCa(c.amountVnd)}đ, creator nhận {formatCa(c.netCa)} Ca
                  {c.sourceKind === 'TopUpCa' && c.sourceCa != null ? `, trừ ${formatCa(c.sourceCa)} Ca nạp` : ', trừ ví doanh nghiệp'}
                </li>
              ))}
            </ul>
          )}
          {s.revokeReason && <p className="mt-1 text-xs text-gray-500">Lý do gỡ: {s.revokeReason}</p>}
          {!s.revokedAt && isSuperAdmin() && (
            <button
              type="button"
              onClick={() => openRevoke(s)}
              className="mt-2 rounded border border-emerald-600 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
            >
              Gỡ lần phạt này
            </button>
          )}
        </div>
      ))}

      {revoking && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4" onClick={() => setRevoking(null)}>
          <div
            className="w-full max-w-[440px] space-y-3 rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold text-slate-800 dark:text-white">Gỡ lần phạt {sanctionLevelLabel(revoking.level).toLowerCase()}</h4>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              Gỡ khoá vĩnh viễn sẽ mở lại tài khoản và gỡ các mục danh sách đen sinh ra từ lần phạt này. Tiền bồi thường đã trả không
              được hoàn lại.
            </p>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Lý do gỡ (10–500 ký tự) <span className="text-red-500">*</span>
              <textarea
                aria-label="Lý do gỡ"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={3}
                className={`${INPUT} resize-none`}
              />
            </label>
            <AdminTotpInput value={totp} onChange={setTotp} />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRevoking(null)}
                className="h-10 min-w-[90px] rounded-md border border-gray-300 px-4 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Huỷ
              </button>
              <button
                type="button"
                disabled={busy || totp.length !== 6 || !!validateAdminNote(reason)}
                onClick={confirmRevoke}
                className="h-10 min-w-[130px] rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Gỡ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSanctionHistory;
